import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Calculator, MessageSquare, ArrowRight, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Header from './Header';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Pricing.css';

// Set REACT_APP_INDY_AI_PRICE_MONTHLY=0 for $0 testing (must match backend INDY_AI_PRICE_CENTS)
const INDY_AI_PRICE_MONTHLY = parseInt(process.env.REACT_APP_INDY_AI_PRICE_MONTHLY || '20', 10);

const Pricing = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [indyCheckoutLoading, setIndyCheckoutLoading] = useState(false);
  const [indySubscription, setIndySubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  useEffect(() => {
    if (searchParams.get('error') === 'subscription_required') {
      toast('Subscribe to Indy AI to access chat.', { icon: '🔒', duration: 6000 });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoadingSubscription(true);
    api.get(`/api/stripe/subscription/${user.id}`)
      .then((res) => { if (!cancelled) setIndySubscription(res.data); })
      .catch(() => { if (!cancelled) setIndySubscription({ hasSubscription: false }); })
      .finally(() => { if (!cancelled) setLoadingSubscription(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const hasActiveIndySubscription =
    indySubscription?.hasSubscription &&
    indySubscription?.subscription?.status === 'active';

  const handleIndyCheckout = async () => {
    if (indyCheckoutLoading) return;
    if (!isAuthenticated || !user) {
      toast('Please sign up or log in to subscribe.', { icon: '🔒', duration: 4000 });
      navigate('/login?redirect=/pricing');
      return;
    }
    setIndyCheckoutLoading(true);
    try {
      const response = await api.post('/api/stripe/create-indy-checkout-session', {});
      if (response.data?.url) {
        window.location.href = response.data.url;
        return;
      }
      toast.error('Failed to start checkout');
    } catch (error) {
      if (error.response?.status === 401) {
        toast('Please sign up or log in to subscribe.', { icon: '🔒', duration: 4000 });
        navigate('/login?redirect=/pricing');
        return;
      }
      const err = error.response?.data?.error ?? error.response?.data;
      const msg = typeof err === 'string' ? err : (err?.message ?? error.message ?? 'Failed to start checkout');
      toast.error(msg);
    } finally {
      setIndyCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
      <Header />
      
      <main className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" style={{color: '#f9fafb'}}>
              Our Services
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-16" style={{color: '#d1d5db'}}>
              Explore our comprehensive range of services designed to meet your business needs and enhance your productivity.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Colo by the U */}
            <div 
              className="service-card group"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '1rem',
                padding: '2.5rem',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(16, 185, 129, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div className="flex justify-center mb-6">
                <div 
                  className="service-icon"
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <Calculator className="h-10 w-10" style={{color: 'white'}} />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center" style={{color: '#f9fafb'}}>
                Colo by the U
              </h3>
              <p className="text-center mb-6 leading-relaxed" style={{color: '#d1d5db'}}>
                Calculate your colocation needs and find the perfect server solution for your business with our intuitive calculator.
              </p>
              <Link 
                to="/colo-calculator" 
                className="service-button group/btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.875rem 1.5rem',
                  borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  marginTop: 'auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Calculate Now
                <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Indy AI */}
            <div 
              className="service-card group"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '1rem',
                padding: '2.5rem',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(139, 92, 246, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div className="flex justify-center mb-6">
                <div 
                  className="service-icon"
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 10px 30px rgba(139, 92, 246, 0.3)'
                  }}
                >
                  <MessageSquare className="h-10 w-10" style={{color: 'white'}} />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center" style={{color: '#f9fafb'}}>
                Indy AI
              </h3>
              <p className="text-center mb-2 leading-relaxed" style={{color: '#d1d5db'}}>
                An intelligent chat assistant designed to help with questions, tasks, and content creation powered by advanced AI.
              </p>
              <p className="text-center mb-6 text-lg font-semibold" style={{color: '#f9fafb'}}>
                {INDY_AI_PRICE_MONTHLY === 0 ? 'Free' : `$${INDY_AI_PRICE_MONTHLY}/month`}
              </p>
              {loadingSubscription && user ? (
                <p className="text-center" style={{ color: '#94a3b8', marginTop: 'auto' }}>Checking subscription...</p>
              ) : hasActiveIndySubscription ? (
                <div style={{ marginTop: 'auto', textAlign: 'center' }}>
                  <p className="text-center mb-3" style={{ color: '#86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <CheckCircle size={20} />
                    You're already subscribed to Indy AI
                  </p>
                  <Link
                    to="/dashboard"
                    className="service-button group/btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      padding: '0.875rem 1.5rem',
                      borderRadius: '0.5rem',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                      color: 'white',
                      textDecoration: 'none',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Open Indy AI
                    <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleIndyCheckout}
                  disabled={indyCheckoutLoading}
                  className="service-button group/btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.875rem 1.5rem',
                    borderRadius: '0.5rem',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                    color: 'white',
                    border: 'none',
                    cursor: indyCheckoutLoading ? 'wait' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    marginTop: 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!indyCheckoutLoading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(139, 92, 246, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {indyCheckoutLoading ? 'Redirecting...' : !isAuthenticated ? 'Sign up to subscribe' : INDY_AI_PRICE_MONTHLY === 0 ? 'Subscribe — Free' : `Subscribe — $${INDY_AI_PRICE_MONTHLY}/mo`}
                  <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;

